import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { campaignRepository } from '../repositories/campaign.repository';
import { campaignKeys } from '../services/campaign.keys';
import { toast } from '@/hooks/use-toast';

export const useCampaignInfluencers = (campaignId: string) => {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: campaignKeys.influencers(campaignId),
    queryFn: async () => {
      return campaignRepository.getCampaignInfluencers(campaignId, currentOrganization?.id);
    },
    enabled: !!campaignId,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useAddInfluencersToCampaign = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      campaignId,
      influencerIds,
    }: {
      campaignId: string;
      influencerIds: string[];
    }) => {
      return campaignRepository.addInfluencersToCampaign(
        campaignId,
        influencerIds,
        currentOrganization?.id
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.influencers(variables.campaignId) });
      toast({
        title: 'Success',
        description: `${variables.influencerIds.length} influencer(s) added to campaign`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to add influencers to campaign',
        variant: 'destructive',
      });
      console.error('Error adding influencers to campaign:', error);
    },
  });
};
